import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalogV3,
  expandDeckDefinition,
  validateDeck,
  type ArmyUpkeep,
  type CardCatalog,
  type DeckDefinition,
  type EngineResourcePool,
  type ResourceCost,
} from "@fate-kingdom/card-data";

import {
  COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG,
  TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS,
} from "./commander-deployment-card-fixtures.js";
import { TEST_COMMAND_ARMY_DEFINITION_IDS } from "./command-card-fixtures.js";
import { TEST_LAND_DEFINITION_IDS } from "./setup-card-fixtures.js";

export const ARMY_DEPLOYMENT_TEST_CARD_DATA_VERSION = "0.6.0-army-deployment.1";

export const TEST_DEPLOYMENT_ARMY_DEFINITION_IDS = {
  smallGuard: "card.test-deployment-army-small-guard",
  balanced: "card.test-deployment-army-balanced",
  heavy: "card.test-deployment-army-heavy",
  expensiveEmergency: "card.test-deployment-army-expensive-emergency",
} as const;

function makeDeploymentArmy(
  id: string,
  name: string,
  cost: ResourceCost,
  upkeep: ArmyUpkeep,
  stats: { readonly armySize: number; readonly attack: number; readonly guard: number },
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "army",
    name,
    cost,
    rulesText: "TEST ONLY: printed Army values for deterministic deployment scenarios.",
    stats,
    upkeep,
    effects: [],
  };
}

/**
 * Isolated test-only content for Army deployment. It extends the frozen
 * Commander-deployment catalog without mutating any historical fixture.
 */
export const ARMY_DEPLOYMENT_TEST_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  ...COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG,
  cardDataVersion: ARMY_DEPLOYMENT_TEST_CARD_DATA_VERSION,
  cards: [
    ...COMMANDER_DEPLOYMENT_TEST_CARD_CATALOG.cards,
    makeDeploymentArmy(
      TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.smallGuard,
      "TEST ONLY Small Guard Army",
      { food: 1 },
      { food: 1 },
      { armySize: 1, attack: 0, guard: 2 },
    ),
    makeDeploymentArmy(
      TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.balanced,
      "TEST ONLY Balanced Army",
      { food: 1, production: 1 },
      { production: 1 },
      { armySize: 1, attack: 2, guard: 1 },
    ),
    makeDeploymentArmy(
      TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.heavy,
      "TEST ONLY Heavy Army",
      { production: 2, culture: 1 },
      { food: 1, production: 2 },
      { armySize: 3, attack: 4, guard: 3 },
    ),
    makeDeploymentArmy(
      TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.expensiveEmergency,
      "TEST ONLY Expensive Emergency Army",
      { science: 2, culture: 1 },
      { food: 4, production: 5 },
      { armySize: 2, attack: 5, guard: 2 },
    ),
  ],
});

export const ARMY_DEPLOYMENT_TEST_ENGINE_CATALOG = createEngineGameplayCatalogV3(
  ARMY_DEPLOYMENT_TEST_CARD_CATALOG,
);

/** Exact typed-resource cases for payment, shortfall and non-wildcard Trade tests. */
export const ARMY_DEPLOYMENT_TEST_RESOURCE_POOLS = {
  smallGuardExact: {
    food: 1,
    production: 0,
    trade: 0,
    science: 0,
    culture: 0,
  },
  balancedExact: {
    food: 1,
    production: 1,
    trade: 0,
    science: 0,
    culture: 0,
  },
  balancedTradeSubstitutionAttempt: {
    food: 1,
    production: 0,
    trade: 1,
    science: 0,
    culture: 0,
  },
  heavyExact: {
    food: 0,
    production: 2,
    trade: 0,
    science: 0,
    culture: 1,
  },
  expensiveEmergencyExact: {
    food: 0,
    production: 0,
    trade: 0,
    science: 2,
    culture: 1,
  },
  expensiveEmergencyShort: {
    food: 0,
    production: 0,
    trade: 0,
    science: 1,
    culture: 1,
  },
} as const satisfies Record<string, EngineResourcePool>;

const armyDeploymentDeckInput = DeckDefinitionSchema.parse({
  schemaVersion: 1,
  definitionVersion: 1,
  cardDataVersion: ARMY_DEPLOYMENT_TEST_CARD_DATA_VERSION,
  id: "deck.test-army-deployment",
  name: "TEST ONLY: Army deployment deck",
  cards: [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.balanced, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.highCapacity, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.lowCapacity, count: 2 },
    { cardId: TEST_DEPLOYMENT_COMMANDER_DEFINITION_IDS.defensive, count: 2 },
    { cardId: TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.smallGuard, count: 3 },
    { cardId: TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.balanced, count: 3 },
    { cardId: TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.heavy, count: 3 },
    { cardId: TEST_DEPLOYMENT_ARMY_DEFINITION_IDS.expensiveEmergency, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.sentries, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.wardens, count: 3 },
    { cardId: "card.test-filler-army-01", count: 2 },
  ],
});

const validatedArmyDeploymentDeck = validateDeck(
  armyDeploymentDeckInput,
  ARMY_DEPLOYMENT_TEST_CARD_CATALOG,
);
if (!validatedArmyDeploymentDeck.success) {
  throw new Error(
    `Invalid test-only Army deployment deck: ${validatedArmyDeploymentDeck.error.message}`,
  );
}

export const ARMY_DEPLOYMENT_TEST_DECK: DeckDefinition = validatedArmyDeploymentDeck.data;
export const ARMY_DEPLOYMENT_TEST_DECK_CARD_IDS = expandDeckDefinition(ARMY_DEPLOYMENT_TEST_DECK);
